import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { getMyTutorAnalytics } from '../../tutor/api/tutorAnalyticsApi';
import { getWritingChecks } from '../../modules/learning/api/writing';
import type { TutorAnalyticsSummary, TrendPoint, DailyEngagementDoc, SpeakingMetricsDoc, TutorSessionDoc } from '../../tutor/types/tutorAnalytics';
import type { WritingCheckListItem } from '../../modules/learning/api/writing';
import { TutorTrendChart } from '../../components/ai-tutor/analytics/TutorTrendChart';
import { BarChartCard } from '../../components/ai-tutor/analytics/BarChartCard';
import { PieChartCard } from '../../components/ai-tutor/analytics/PieChartCard';
import { AnalyticsReportView } from './AnalyticsReportView';
import { exportElementToPdf } from '../../utils/pdfExport';
import { showSuccessToast, showErrorToast } from '../../utils/errorHandler';
import { ROUTES } from '../../utils/constants';
import { ArrowLeft, RefreshCw, AlertTriangle, Loader2, Download } from 'lucide-react';

const DAY_OPTIONS = [7, 14, 30, 90] as const;

// -------- Line chart for writing score/suggestions over checks --------
function WritingSeriesChart({
  title,
  subtitle,
  points,
  valueLabel,
  emptyMessage = 'No data',
}: {
  title: string;
  subtitle?: string;
  points: { label: string; value: number }[];
  valueLabel?: string;
  emptyMessage?: string;
}) {
  if (!points.length) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{title}</CardTitle>
          {subtitle && <p className="text-sm text-app-text-secondary dark:text-gray-400">{subtitle}</p>}
        </CardHeader>
        <CardContent>
          <div className="h-64 flex items-center justify-center text-app-text-secondary dark:text-gray-400">{emptyMessage}</div>
        </CardContent>
      </Card>
    );
  }
  const width = 400;
  const height = 200;
  const margin = { top: 20, right: 20, bottom: 40, left: 44 };
  const chartW = width - margin.left - margin.right;
  const chartH = height - margin.top - margin.bottom;
  const values = points.map(p => p.value);
  const maxV = Math.max(...values, 1);
  const yScale = (v: number) => chartH - (v / maxV) * chartH;
  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${margin.left + (i / Math.max(1, points.length - 1)) * chartW} ${margin.top + yScale(p.value)}`).join(' ');

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{title}</CardTitle>
        {subtitle && <p className="text-sm text-app-text-secondary dark:text-gray-400">{subtitle}</p>}
      </CardHeader>
      <CardContent>
        <svg width={width} height={height} className="w-full">
          <path d={pathD} fill="none" stroke="currentColor" strokeWidth={2} className="text-blue-500 dark:text-blue-400" />
          {points.map((p, i) => {
            const x = margin.left + (i / Math.max(1, points.length - 1)) * chartW;
            const y = margin.top + yScale(p.value);
            return (
              <g key={i}>
                <circle cx={x} cy={y} r={4} className="fill-blue-500 dark:fill-blue-400" />
                <text x={x} y={height - 8} textAnchor="middle" className="text-xs fill-app-text-secondary dark:fill-gray-400">
                  {p.label}
                </text>
              </g>
            );
          })}
          <text x={margin.left - 8} y={margin.top} textAnchor="end" className="text-xs fill-app-text-secondary dark:fill-gray-400">
            {valueLabel ?? ''}
          </text>
        </svg>
      </CardContent>
    </Card>
  );
}

export const StudentAnalyticsChartsPage: React.FC = () => {
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [tutorData, setTutorData] = useState<TutorAnalyticsSummary | null>(null);
  const [tutorLoading, setTutorLoading] = useState(true);
  const [tutorError, setTutorError] = useState<string | null>(null);
  const [writingItems, setWritingItems] = useState<WritingCheckListItem[]>([]);
  const [writingLoading, setWritingLoading] = useState(true);
  const [writingError, setWritingError] = useState<string | null>(null);
  const [showTutorCharts, setShowTutorCharts] = useState(true);
  const [showWritingCharts, setShowWritingCharts] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const reportContainerRef = useRef<HTMLDivElement>(null);

  const fetchTutor = useCallback(async () => {
    setTutorLoading(true);
    setTutorError(null);
    try {
      const data = await getMyTutorAnalytics(days);
      setTutorData({
        ok: data.ok ?? true,
        total_tutor_minutes: data.total_tutor_minutes ?? 0,
        sessions_count: data.sessions_count ?? 0,
        last_active_at: data.last_active_at ?? null,
        latest_speaking_level: data.latest_speaking_level ?? null,
        latest_overall_score: data.latest_overall_score ?? null,
        trend: data.trend ?? [],
        last_lesson_no: data.last_lesson_no ?? null,
        last_topic_label: data.last_topic_label ?? null,
        speaking_sessions: data.speaking_sessions ?? [],
        daily_engagement: data.daily_engagement ?? [],
        session_history: data.session_history ?? [],
      });
    } catch (e: unknown) {
      setTutorError(e instanceof Error ? e.message : 'Failed to load tutor analytics');
      setTutorData(null);
    } finally {
      setTutorLoading(false);
    }
  }, [days]);

  const fetchWriting = useCallback(async () => {
    setWritingLoading(true);
    setWritingError(null);
    try {
      const res = await getWritingChecks({ limit: 20, skip: 0 });
      setWritingItems(res.items ?? []);
    } catch (e: unknown) {
      setWritingError(e instanceof Error ? e.message : 'Failed to load writing checks');
      setWritingItems([]);
    } finally {
      setWritingLoading(false);
    }
  }, []);

  const refresh = useCallback(() => {
    fetchTutor();
    fetchWriting();
  }, [fetchTutor, fetchWriting]);

  useEffect(() => {
    fetchTutor();
  }, [fetchTutor]);
  useEffect(() => {
    fetchWriting();
  }, [fetchWriting]);

  useEffect(() => {
    if (!isExporting || !reportContainerRef.current) return;
    const timer = setTimeout(() => {
      if (!reportContainerRef.current) {
        setIsExporting(false);
        return;
      }
      exportElementToPdf(reportContainerRef.current, {
        filename: 'student-analytics-report',
        delayBeforeCapture: 0,
      })
        .then(() => showSuccessToast('Report downloaded'))
        .catch((err) => showErrorToast(err instanceof Error ? err : String(err)))
        .finally(() => setIsExporting(false));
    }, 500);
    return () => clearTimeout(timer);
  }, [isExporting]);

  // Derived: daily engagement bar (sessions per day)
  const dailyEngagementBars = useMemo(() => {
    const d = tutorData?.daily_engagement ?? [];
    return d
      .slice()
      .sort((a, b) => (a.date < b.date ? -1 : 1))
      .map((x: DailyEngagementDoc) => ({
        label: new Date(x.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
        sessions: x.sessions ?? 0,
        tutor_minutes: x.tutor_minutes ?? 0,
      }));
  }, [tutorData]);

  // Derived: speaking category averages
  const speakingAverages = useMemo(() => {
    const sessions = tutorData?.speaking_sessions ?? [];
    if (!sessions.length) return [];
    const keys = ['pronunciation', 'fluency', 'grammar', 'vocabulary', 'overall'] as const;
    return keys.map(key => {
      const sum = sessions.reduce((s, x) => s + ((x.scores as Record<string, number>)?.[key] ?? 0), 0);
      return { label: key.charAt(0).toUpperCase() + key.slice(1), value: Math.round((sum / sessions.length) * 10) / 10 };
    });
  }, [tutorData]);

  // Derived: session_history tutor_type counts
  const tutorTypeCounts = useMemo(() => {
    const hist = tutorData?.session_history ?? [];
    const map = new Map<string, number>();
    hist.forEach((s: TutorSessionDoc) => {
      const t = s.tutor_type || 'Unknown';
      map.set(t, (map.get(t) ?? 0) + 1);
    });
    return Array.from(map.entries()).map(([label, value]) => ({ label, value }));
  }, [tutorData]);

  // Derived: session_history mode counts
  const sessionModeCounts = useMemo(() => {
    const hist = tutorData?.session_history ?? [];
    const map = new Map<string, number>();
    hist.forEach((s: TutorSessionDoc) => {
      const m = s.mode || 'Unknown';
      map.set(m, (map.get(m) ?? 0) + 1);
    });
    return Array.from(map.entries()).map(([label, value]) => ({ label, value }));
  }, [tutorData]);

  // Writing: score per check (by date)
  const writingScorePerCheck = useMemo(() => {
    const items = writingItems
      .filter(i => i.created_at)
      .slice()
      .sort((a, b) => (a.created_at! < b.created_at! ? -1 : 1))
      .slice(-15);
    return items.map(item => {
      const score = item.derived_metadata?.overall_score ?? item.result?.rubric?.overallScore ?? 0;
      return {
        label: new Date(item.created_at!).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
        value: Math.max(0, Math.min(100, score)),
      };
    });
  }, [writingItems]);

  // Writing: rubric averages (derived_metadata or result.rubric)
  const writingRubricAverages = useMemo(() => {
    const items = writingItems;
    if (!items.length) return [];
    type RubricNumericKey = 'grammar' | 'clarity' | 'vocabulary' | 'overallScore';
    const config: { key: keyof NonNullable<WritingCheckListItem['derived_metadata']>; rubricKey: RubricNumericKey; label: string }[] = [
      { key: 'grammar_score', rubricKey: 'grammar', label: 'Grammar' },
      { key: 'clarity_score', rubricKey: 'clarity', label: 'Clarity' },
      { key: 'vocabulary_score', rubricKey: 'vocabulary', label: 'Vocabulary' },
      { key: 'overall_score', rubricKey: 'overallScore', label: 'Overall' },
    ];
    return config.map(({ key, rubricKey, label }) => {
      const sum = items.reduce((s, item) => {
        const v = item.derived_metadata?.[key] ?? item.result?.rubric?.[rubricKey];
        return s + (typeof v === 'number' ? v : 0);
      }, 0);
      return { label, value: Math.round((sum / items.length) * 10) / 10 };
    });
  }, [writingItems]);

  // Writing: suggestions per check
  const writingSuggestionsPerCheck = useMemo(() => {
    const items = writingItems
      .filter(i => i.created_at)
      .slice()
      .sort((a, b) => (a.created_at! < b.created_at! ? -1 : 1))
      .slice(-15);
    return items.map(item => ({
      label: new Date(item.created_at!).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
      value: item.derived_metadata?.suggestion_count ?? 0,
    }));
  }, [writingItems]);

  // Writing: mode distribution
  const writingModeCounts = useMemo(() => {
    const map = new Map<string, number>();
    writingItems.forEach(item => {
      const m = item.mode || 'practice';
      map.set(m, (map.get(m) ?? 0) + 1);
    });
    return Array.from(map.entries()).map(([label, value]) => ({ label, value }));
  }, [writingItems]);

  const trend: TrendPoint[] = tutorData?.trend ?? [];

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(ROUTES.STUDENT_ANALYTICS)} className="inline-flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Analytics Charts</h1>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-app-text-secondary dark:text-gray-400">Days:</span>
            {DAY_OPTIONS.map(d => (
              <Button
                key={d}
                variant={days === d ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => setDays(d)}
              >
                {d}
              </Button>
            ))}
            <Button variant="outline" size="sm" onClick={refresh} disabled={tutorLoading || writingLoading}>
              <RefreshCw className={`w-4 h-4 ${tutorLoading || writingLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setIsExporting(true)} disabled={isExporting}>
              {isExporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              {isExporting ? 'Preparing…' : 'Export report'}
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            variant={showTutorCharts ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setShowTutorCharts(!showTutorCharts)}
          >
            Tutor charts
          </Button>
          <Button
            variant={showWritingCharts ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setShowWritingCharts(!showWritingCharts)}
          >
            Writing charts
          </Button>
        </div>

        {tutorError && (
          <Card className="border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20">
            <CardContent className="pt-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              <span className="text-sm text-amber-800 dark:text-amber-200">{tutorError}</span>
              <Button size="sm" variant="outline" onClick={fetchTutor}>Retry</Button>
            </CardContent>
          </Card>
        )}
        {writingError && (
          <Card className="border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20">
            <CardContent className="pt-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              <span className="text-sm text-amber-800 dark:text-amber-200">{writingError}</span>
              <Button size="sm" variant="outline" onClick={fetchWriting}>Retry</Button>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {showTutorCharts && (
            <>
              {tutorLoading ? (
                <Card><CardContent className="h-64 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-app-text-secondary" /></CardContent></Card>
              ) : (
                <TutorTrendChart trend={trend} />
              )}
              {!tutorLoading && (
                <BarChartCard
                  title="Daily engagement (sessions)"
                  subtitle="Sessions per day"
                  data={dailyEngagementBars}
                  getLabel={d => d.label}
                  getValue={d => d.sessions}
                  emptyMessage="No daily engagement data"
                  orientation="vertical"
                />
              )}
              {!tutorLoading && (
                <BarChartCard
                  title="Speaking score breakdown"
                  subtitle="Average by category"
                  data={speakingAverages}
                  getLabel={d => d.label}
                  getValue={d => d.value}
                  valueFormat={v => `${v}%`}
                  emptyMessage="No speaking session data"
                />
              )}
              {!tutorLoading && (
                <BarChartCard
                  title="Daily engagement (tutor minutes)"
                  subtitle="Minutes per day"
                  data={dailyEngagementBars}
                  getLabel={d => d.label}
                  getValue={d => d.tutor_minutes}
                  valueFormat={v => `${v} min`}
                  emptyMessage="No daily engagement data"
                />
              )}
              {!tutorLoading && (
                <PieChartCard
                  title="Session tutor type"
                  subtitle="From session history"
                  segments={tutorTypeCounts}
                  emptyMessage="No session history"
                />
              )}
              {/* {!tutorLoading && sessionModeCounts.length > 0 && (
                <PieChartCard
                  title="Session mode"
                  subtitle="Voice vs text"
                  segments={sessionModeCounts}
                  emptyMessage="No session history"
                />
              )} */}
            </>
          )}

          {showWritingCharts && (
            <>
              {writingLoading ? (
                <Card><CardContent className="h-64 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-app-text-secondary" /></CardContent></Card>
              ) : (
                <>
                  <BarChartCard
                    title="Writing score per check"
                    subtitle="Latest checks by date"
                    data={writingScorePerCheck}
                    getLabel={d => d.label}
                    getValue={d => d.value}
                    valueFormat={v => `${v}%`}
                    emptyMessage="No writing checks"
                    barColor="text-emerald-500 dark:text-emerald-400"
                  />
                  <BarChartCard
                    title="Writing rubric averages"
                    subtitle="Across all loaded checks"
                    data={writingRubricAverages}
                    getLabel={d => d.label}
                    getValue={d => d.value}
                    valueFormat={v => `${v}%`}
                    emptyMessage="No rubric data"
                    barColor="text-violet-500 dark:text-violet-400"
                  />
                  <WritingSeriesChart
                    title="Suggestions per check"
                    subtitle="By check date"
                    points={writingSuggestionsPerCheck}
                    emptyMessage="No writing checks"
                  />
                  {/* <PieChartCard
                    title="Writing mode distribution"
                    subtitle="Check mode"
                    segments={writingModeCounts}
                    emptyMessage="No writing checks"
                  /> */}
                </>
              )}
            </>
          )}
        </div>

        {isExporting && (
          <div
            ref={reportContainerRef}
            style={{ position: 'fixed', left: '-9999px', top: 0, zIndex: -1, width: 794 }}
            aria-hidden
          >
            <AnalyticsReportView
              tutorData={tutorData}
              writingItems={writingItems}
              days={days}
            />
          </div>
        )}
      </div>
    </Layout>
  );
};
